import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { FollowupFields, downloadBlob } from "../components/FollowupFields";
import { FinalDecision, DetailsToggle } from "../components/FinalDecision";
import { PrescribingAlert } from "../components/PrescribingAlert";
import { CaseEmergencies } from "../components/emergency/CaseEmergencies";
import { CartridgeSize, useCartridgeSize } from "../components/CartridgeSize";
import { TreatmentPlan } from "../components/TreatmentPlan";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RecommendationCard, RISK, MedicalDisclaimer } from "../components/clinical";
import { toast } from "sonner";
import { Search, X, Plus, Stethoscope, FlaskConical, Save, Download, RefreshCw, Loader2, ClipboardList, Syringe, Lightbulb } from "lucide-react";

function ItemPicker({ label, icon: Icon, items, selected, onChange, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    const f = items.filter((i) => i.name.toLowerCase().includes(t) || (i.aka || []).some((a) => a.toLowerCase().includes(t)));
    const groups = {};
    f.forEach((i) => { (groups[i.category] = groups[i.category] || []).push(i); });
    return groups;
  }, [items, q]);

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const nameOf = (id) => items.find((i) => i.id === id)?.name || id;

  return (
    <div>
      <Label className="flex items-center gap-2 text-slate-700"><Icon className="w-4 h-4 text-sky-600" /> {label}</Label>
      <div className="mt-2 flex flex-wrap gap-2 mb-2" data-testid={`${testid}-chips`}>
        {selected.length === 0 && <span className="text-sm text-slate-400">None selected</span>}
        {selected.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 bg-sky-50 text-sky-800 text-sm px-2.5 py-1 rounded-full">
            {nameOf(id)}
            <button onClick={() => toggle(id)} data-testid={`${testid}-remove-${id}`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid={`${testid}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={`Search and add ${label.toLowerCase()}...`}
          className="pl-9"
        />
      </div>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto hide-scrollbar shadow-sm">
          {Object.keys(filtered).length === 0 && <p className="text-sm text-slate-400 p-3">No matches</p>}
          {q.trim().length > 1 && (
            <Link
              to={`/app/suggest?kind=${testid === "conditions" ? "condition" : "medication"}&name=${encodeURIComponent(q.trim())}`}
              data-testid={`${testid}-suggest-link`}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-sky-700 font-medium border-t border-slate-100 hover:bg-sky-50"
            >
              <Lightbulb className="w-4 h-4" /> Not listed? Suggest “{q.trim()}” to the DentiCision team
            </Link>
          )}
          {Object.entries(filtered).map(([cat, arr]) => (
            <div key={cat}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-3 pt-2.5 pb-1">{cat}</p>
              {arr.map((i) => (
                <button
                  key={i.id}
                  data-testid={`${testid}-option-${i.id}`}
                  onClick={() => toggle(i.id)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${selected.includes(i.id) ? "text-sky-700 font-medium" : "text-slate-700"}`}
                >
                  {i.name}
                  {selected.includes(i.id) ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5 text-slate-400" />}
                </button>
              ))}
            </div>
          ))}
          <button onClick={() => setOpen(false)} className="w-full text-xs text-slate-500 py-2 border-t border-slate-100 hover:bg-slate-50">Close</button>
        </div>
      )}
    </div>
  );
}

const FU_LABEL = {
  antiresorptive_years: "Years on antiresorptive", inr: "INR", hba1c: "HbA1c (%)", anc: "ANC (x10⁹/L)", platelets: "Platelets (x10⁹/L)",
  bp_systolic: "Systolic BP", bp_diastolic: "Diastolic BP", prednisolone_mg: "Prednisolone (mg/day)",
  stent_days_ago: "Days since stent", mi_days_ago: "Days since MI", stroke_days_ago: "Days since CVA",
  radiation_gy: "Radiation dose (Gy)", egfr: "eGFR (mL/min/1.73m²)", wbc: "WBC (x10⁹/L)",
  transplant_months_ago: "Months since transplant", biologic_last_dose_days: "Days since last biologic dose",
  cardiac_surgery_days_ago: "Days since cardiac surgery", pregnancy_trimester: "Which trimester is the patient in?",
};

const TIMING_FIELDS = ["stent_days_ago", "mi_days_ago", "stroke_days_ago", "radiation_gy", "transplant_months_ago", "biologic_last_dose_days", "cardiac_surgery_days_ago", "pregnancy_trimester", "implant_count", "antiresorptive_years"];

export default function NewEvaluation() {
  const [catalog, setCatalog] = useState({ conditions: [], medications: [], procedures: [] });
  const [anestheticOptions, setAnestheticOptions] = useState([]);
  const [patient, setPatient] = useState({ name: "", age: "", sex: "", weight: "", phone: "", file_no: "" });
  const [conditions, setConditions] = useState([]);
  const [medications, setMedications] = useState([]);
  const [procedureIds, setProcedureIds] = useState([]);
  const procedureId = procedureIds[0] || "";
  const toggleProc = (id) => setProcedureIds((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  const [anestheticId, setAnestheticId] = useState("");
  const [cartridgeMl, setCartridgeMl] = useCartridgeSize();
  const [labs, setLabs] = useState({});
  const [timing, setTiming] = useState({});
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [referralQuestion, setReferralQuestion] = useState("");
  const [labDefs, setLabDefs] = useState([]);
  const [labUnits, setLabUnits] = useState({});
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setCatalog(data)).catch(() => toast.error("Failed to load catalog"));
    api.get("/anesthetic/options").then(({ data }) => setAnestheticOptions(data.anesthetics)).catch(() => {});
    api.get("/labs/definitions").then(({ data }) => setLabDefs(data.labs)).catch(() => {});
  }, []);

  const combined = { ...labs, ...timing };
  const setField = (key) => (e) => {
    const v = e.target.value;
    if (TIMING_FIELDS.includes(key)) {
      setTiming((t) => ({ ...t, [key]: v }));
    } else {
      setLabs((l) => ({ ...l, [key]: v }));
    }
  };

  const assess = async () => {
    if (!procedureId) return toast.error("Select at least one planned procedure");
    if (conditions.length === 0 && medications.length === 0) return toast.error("Add at least one condition or medication");
    setLoading(true);
    setSavedId(null);
    try {
      const { data } = await api.post("/evaluate", { conditions, medications, procedure_id: procedureId, procedure_ids: procedureIds, labs, lab_units: labUnits, timing, weight_kg: patient.weight ? Number(patient.weight) : null, anesthetic_id: anestheticId, cartridge_ml: cartridgeMl, age: patient.age ? Number(patient.age) : null });
      setDecision(data);
    } catch (err) {
      toast.error(err.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : "Evaluation failed");
    } finally {
      setLoading(false);
    }
  };

  const saveCase = async () => {
    setSaving(true);
    try {
      if (pendingCaseId) {
        const { data } = await api.put(`/cases/${pendingCaseId}/results`, { labs, lab_units: labUnits, timing });
        setSavedId(data.id);
        if (data.status !== "pending") setPendingCaseId(null);
        toast.success(data.status === "pending" ? "Pending case updated" : "Results saved — case completed");
        return data;
      }
      const { data } = await api.post("/cases", {
        patient: { name: patient.name, sex: patient.sex, age: patient.age ? Number(patient.age) : null, weight_kg: patient.weight ? Number(patient.weight) : null, phone: patient.phone.trim(), file_no: patient.file_no.trim() },
        conditions, medications, procedure_id: procedureId, procedure_ids: procedureIds, labs, lab_units: labUnits, timing, referral_question: referralQuestion,
        weight_kg: patient.weight ? Number(patient.weight) : null, anesthetic_id: anestheticId, cartridge_ml: cartridgeMl,
      });
      setSavedId(data.id);
      if (data.status === "pending" && !data.awaiting_clearance) setPendingCaseId(data.id);
      toast.success(data.awaiting_clearance ? "Saved to Pending Cases — awaiting specialist approval" : data.status === "pending" ? "Saved to Pending Cases — awaiting results" : "Case saved to history");
      return data;
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save case");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const savePendingAndExport = async () => {
    if (!patient.name.trim()) return toast.error("Enter the patient name for the lab request");
    const data = savedId ? { id: savedId } : await saveCase();
    if (!data) return;
    try {
      await downloadBlob(api, `/cases/${data.id}/lab-request-pdf`, `lab_request_${(patient.name || data.id).replace(/\s+/g, "_")}.pdf`);
    } catch {
      toast.error("Could not generate lab request");
    }
  };

  const labIds = labDefs.map((d) => d.id);
  const pendingLabs = (decision?.followups || []).filter((f) => labIds.includes(f.id));

  const downloadPdf = async () => {
    if (!savedId) return;
    try {
      const res = await api.get(`/cases/${savedId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `referral_${savedId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  const overall = decision?.overall_status;
  const r = overall ? RISK[overall] : null;

  return (
    <div data-testid="new-evaluation-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">New Evaluation</h1>
      <p className="text-slate-500 mt-1">Enter the patient's medical picture and today's planned procedure.</p>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* Left: inputs */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Patient details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="pname">Name</Label>
                <Input id="pname" data-testid="patient-name" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="mt-1.5" placeholder="Patient name" />
              </div>
              <div>
                <Label htmlFor="pphone">Mobile (optional)</Label>
                <Input id="pphone" data-testid="patient-phone" type="tel" dir="ltr" value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} className="mt-1.5" placeholder="05XXXXXXXX" />
              </div>
              <div>
                <Label htmlFor="pfile">Clinic file no. (optional)</Label>
                <Input id="pfile" data-testid="patient-file-no" value={patient.file_no} onChange={(e) => setPatient({ ...patient, file_no: e.target.value })} className="mt-1.5" placeholder="e.g. 10234" />
              </div>
              <div>
                <Label htmlFor="page">Age</Label>
                <Input id="page" data-testid="patient-age" type="number" value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value })} className="mt-1.5" placeholder="45" />
              </div>
              <div>
                <Label htmlFor="pweight">Weight (kg)</Label>
                <Input id="pweight" data-testid="patient-weight" type="number" value={patient.weight} onChange={(e) => setPatient({ ...patient, weight: e.target.value })} className="mt-1.5" placeholder="70" />
              </div>
              <div className="col-span-2">
                <Label>Sex</Label>
                <Select value={patient.sex} onValueChange={(v) => setPatient({ ...patient, sex: v })}>
                  <SelectTrigger data-testid="patient-sex" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5">
            <ItemPicker label="Medical conditions" icon={Stethoscope} items={catalog.conditions} selected={conditions} onChange={setConditions} testid="conditions" />
            <ItemPicker label="Medications" icon={FlaskConical} items={catalog.medications} selected={medications} onChange={setMedications} testid="medications" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <Label>Planned procedures <span className="text-slate-400 font-normal">(select one or more — treatment plan)</span></Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="procedure-select">
              {catalog.procedures.map((p) => {
                const on = procedureIds.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggleProc(p.id)} data-testid={`procedure-${p.id}`} aria-pressed={on}
                    className={`text-left text-sm px-3 py-1.5 rounded-lg border transition-colors ${on ? "bg-sky-700 border-sky-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-sky-400"}`}>
                    {on && "✓ "}{p.name}
                  </button>
                );
              })}
            </div>

            <Label className="flex items-center gap-2 mt-4"><Syringe className="w-4 h-4 text-sky-600" /> Local anaesthetic (optional)</Label>
            <Select value={anestheticId} onValueChange={setAnestheticId}>
              <SelectTrigger data-testid="anesthetic-select" className="mt-1.5"><SelectValue placeholder="Select agent for max-dose calc" /></SelectTrigger>
              <SelectContent>
                {anestheticOptions.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`anes-opt-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1.5">Uses the patient's weight and cardiac conditions to compute the max safe dose &amp; adrenaline limit.</p>
            {anestheticId && <div className="mt-3"><CartridgeSize value={cartridgeMl} onChange={setCartridgeMl} /></div>}
          </div>

          <Button onClick={assess} disabled={loading} data-testid="assess-btn" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-base">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ClipboardList className="w-5 h-5 mr-2" /> Assess risk</>}
          </Button>
        </div>

        {/* Right: results */}
        <div className="space-y-5">
          {!decision ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center h-full flex flex-col items-center justify-center">
              <ClipboardList className="w-10 h-10 text-slate-300" />
              <p className="text-slate-500 mt-3">The colour-coded recommendation will appear here.</p>
            </div>
          ) : (
            <div data-testid="decision-result">
              <p className="sr-only" data-testid="overall-status">{r.label}</p>
              <PrescribingAlert p={decision.prescribing} />
              <TreatmentPlan plan={decision.plan} />
              <FinalDecision fd={decision.final_decision} procedureName={decision.procedure?.name} />

              {decision.followups?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-4" data-testid="followups">
                  <h4 className="font-heading font-semibold text-amber-900">Follow-up data needed</h4>
                  <p className="text-sm text-amber-800 mt-0.5">Enter these values, then re-assess for a definitive decision. Each item explains why it's needed and what to look for.</p>
                  <FollowupFields
                    followups={decision.followups} values={combined} units={labUnits} labDefs={labDefs} labels={FU_LABEL}
                    onValue={(id, v) => setField(id)({ target: { value: v } })}
                    onUnit={(id, u) => setLabUnits((x) => ({ ...x, [id]: u }))}
                  />
                  <Button onClick={assess} data-testid="reassess-btn" className="mt-4 bg-amber-600 hover:bg-amber-700">
                    <RefreshCw className="w-4 h-4 mr-2" /> Re-assess with values
                  </Button>
                </div>
              )}

              {pendingLabs.length > 0 && (
                <div data-testid="lab-request-card" className="bg-sky-50 border border-sky-200 rounded-xl p-6 mt-4">
                  <h4 className="font-heading font-semibold text-sky-900 flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Laboratory tests required</h4>
                  <p className="text-sm text-sky-800 mt-0.5">Results not available yet? Export a lab request for the patient. The case is saved as <b>pending</b> so you can enter the results when the patient returns.</p>
                  <ul className="mt-3 space-y-1.5">
                    {pendingLabs.map((f) => {
                      const d = labDefs.find((x) => x.id === f.id);
                      return <li key={f.id} className="text-sm text-slate-800">• <b>{d?.name}</b> <span className="text-xs text-slate-500">— {d?.specimen}</span></li>;
                    })}
                  </ul>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <Button onClick={savePendingAndExport} disabled={saving} data-testid="export-lab-request-btn" className="bg-sky-700 hover:bg-sky-800">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Save as pending & export lab request</>}
                    </Button>
                    {savedId && <Button variant="outline" onClick={() => navigate("/app/pending")} data-testid="go-pending-btn">View pending cases</Button>}
                  </div>
                </div>
              )}

              <CaseEmergencies recs={decision.recommendations} />
              <DetailsToggle count={decision.recommendations.length}>
                {decision.recommendations.map((rec) => <RecommendationCard key={rec.item_id} rec={rec} />)}
              </DetailsToggle>
              <MedicalDisclaimer testid="evaluation-medical-disclaimer" />

              {decision.anesthetic && (
                <div data-testid="anesthetic-result" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mt-4">
                  <h4 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Syringe className="w-4 h-4 text-sky-600" /> Local anaesthetic — max dose</h4>
                  <p className="text-sm text-slate-700 mt-2">{decision.anesthetic.anesthetic}</p>
                  <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
                    {decision.anesthetic.max_cartridges} <span className="text-sm font-normal text-slate-500">cartridges of {decision.anesthetic.cartridge_ml} mL (~{decision.anesthetic.max_mg} mg)</span>
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">Limited by: {decision.anesthetic.limiting_factor}</p>
                  {decision.anesthetic.adrenaline && (
                    <p className="text-xs text-slate-500 mt-1">Adrenaline max {decision.anesthetic.adrenaline.limit_mg} mg{decision.anesthetic.adrenaline.cardiac_restricted ? " (cardiac limit)" : ""} ≈ {decision.anesthetic.adrenaline.max_cartridges_by_adrenaline} cartridges</p>
                  )}
                  {decision.anesthetic.warnings?.map((w, i) => <p key={i} className="text-xs text-amber-800 mt-1">• {w}</p>)}
                </div>
              )}

              <p className="text-xs text-slate-400 mt-4 italic border-t border-slate-200 pt-3">{decision.disclaimer}</p>

              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-4">
                <h4 className="font-heading font-semibold text-slate-900">Save & refer</h4>
                {decision.final_decision?.tier === "clearance" && (
                  <p data-testid="clearance-save-hint" className="mt-2 text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                    Specialist approval is required: save the case, download the report and send it. The case waits in <b>Pending Cases</b> until you record the approval — then the decision changes to <b>PROCEED</b> automatically.
                  </p>
                )}
                <Label className="mt-3 block text-sm">Directed question to the treating physician (added to the consultation report)</Label>
                <Textarea
                  data-testid="referral-question"
                  value={referralQuestion}
                  onChange={(e) => setReferralQuestion(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                  placeholder="e.g. Please advise whether warfarin can be safely continued for a surgical extraction."
                />
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button onClick={saveCase} disabled={saving} data-testid="save-case-btn" className="bg-slate-900 hover:bg-slate-800">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save case</>}
                  </Button>
                  {decision.final_decision?.tier !== "pending" && <Button onClick={downloadPdf} disabled={!savedId} variant="outline" data-testid="download-pdf-btn" className="border-slate-300">
                    <Download className="w-4 h-4 mr-2" /> Download consultation report PDF
                  </Button>}
                </div>
                {decision.final_decision?.tier === "pending" ? <p className="text-xs text-slate-500 mt-2" data-testid="report-after-info-hint">The consultation report becomes available once the required information is complete.</p>
                  : !savedId && <p className="text-xs text-slate-400 mt-2">Save the case to enable the consultation report PDF.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
